import React, { useMemo } from 'react';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { useTheme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';

import ChartCard from '~/components/ChartCard';
import LocaleMessage from '~/components/LocaleMessage';

import GetPluginspaceTheme from '~/util/PluginspaceTheme';

import { ChatCardContainer } from '../../Dashboard/styles';
import { formatFluidVolume } from './cleaningReportConsumptionUtils';
import {
    buildConsumptionChartSeries,
    formatEnergyKwh,
    getCleaningReportChartColors,
    seriesFromPuduAnalysis,
    seriesPeak,
    summarizeCleaningConsumption,
} from './cleaningReportUtils';

function formatAxisNumber(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    if (n === 0) return '0';
    if (Math.abs(n) >= 10) return n.toFixed(0);
    if (Math.abs(n) >= 1) return n.toFixed(1);
    return n.toFixed(2);
}

function UtilityResourceCard({
    titleId,
    subtitleId,
    valueText,
    coverage,
    series,
    color,
    gradientId,
    unitLabel,
    tooltipLabel,
    empty,
    darkMode,
    peak,
    xAxisInterval,
}) {
    const tickFill = darkMode ? '#bdbdbd' : '#616161';

    return (
        <div className="col-md-6 col-12 mb-5 dash-card">
            <ChatCardContainer>
                <ChartCard
                    className="animated slideInFromLeft animation-duration-3"
                    title={<LocaleMessage msg={titleId} />}
                    value={valueText}
                    desc={
                        empty ? (
                            <LocaleMessage msg="page.robots.cleaning_report.utility.empty" />
                        ) : (
                            <>
                                <LocaleMessage msg={subtitleId} />
                                {coverage}
                                {peak ? (
                                    <>
                                        {' · '}
                                        <LocaleMessage
                                            msg="page.robots.cleaning_report.utility.peak"
                                            values={{
                                                value: `${formatAxisNumber(
                                                    peak.value
                                                )} ${unitLabel}`,
                                                day: peak.name,
                                            }}
                                        />
                                    </>
                                ) : null}
                            </>
                        )
                    }
                >
                    <div className="row">
                        <div className="col-12 mb-3 area-chart-container">
                            <ResponsiveContainer
                                className="card-img-bottom overflow-hidden animated slideInFromLeft animation-duration-5"
                                width="100%"
                                height={260}
                            >
                                <AreaChart
                                    data={series}
                                    margin={{
                                        top: 16,
                                        right: 16,
                                        left: 8,
                                        bottom: 8,
                                    }}
                                >
                                    <defs>
                                        <linearGradient
                                            id={gradientId}
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop
                                                offset="0%"
                                                stopColor={color}
                                                stopOpacity={0.35}
                                            />
                                            <stop
                                                offset="100%"
                                                stopColor={color}
                                                stopOpacity={0.02}
                                            />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid
                                        vertical={false}
                                        stroke={
                                            darkMode
                                                ? 'rgba(255,255,255,0.08)'
                                                : 'rgba(0,0,0,0.08)'
                                        }
                                    />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 11, fill: tickFill }}
                                        interval={xAxisInterval}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: tickFill }}
                                        tickFormatter={formatAxisNumber}
                                        width={48}
                                        label={{
                                            value: unitLabel,
                                            angle: -90,
                                            position: 'insideLeft',
                                            style: {
                                                fontSize: 11,
                                                fill: tickFill,
                                            },
                                        }}
                                    />
                                    <RechartsTooltip
                                        formatter={v => [
                                            `${formatAxisNumber(
                                                v
                                            )} ${unitLabel}`,
                                            tooltipLabel,
                                        ]}
                                        labelFormatter={label => label}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="value"
                                        stroke={color}
                                        strokeWidth={2}
                                        fill={`url(#${gradientId})`}
                                        dot={{
                                            r: 3,
                                            fill: color,
                                            strokeWidth: 0,
                                        }}
                                        activeDot={{ r: 5, fill: color }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </ChartCard>
            </ChatCardContainer>
        </div>
    );
}

UtilityResourceCard.propTypes = {
    titleId: PropTypes.string.isRequired,
    subtitleId: PropTypes.string.isRequired,
    valueText: PropTypes.string.isRequired,
    coverage: PropTypes.node,
    series: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
        })
    ).isRequired,
    color: PropTypes.string.isRequired,
    gradientId: PropTypes.string.isRequired,
    unitLabel: PropTypes.string.isRequired,
    tooltipLabel: PropTypes.string.isRequired,
    empty: PropTypes.bool,
    darkMode: PropTypes.bool,
    peak: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
    }),
    xAxisInterval: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

UtilityResourceCard.defaultProps = {
    coverage: null,
    empty: false,
    darkMode: false,
    peak: null,
    xAxisInterval: 'preserveStartEnd',
};

function CleaningReportUtilityCharts({
    list,
    periodDates,
    localeCode,
    intl,
    consumption,
}) {
    const settings = useSelector(state => state.settings || null);
    const darkMode = !!(settings && settings.dark_mode);
    const theme = useTheme();
    const colors = GetPluginspaceTheme(theme);
    const { waterColor, energyColor } = getCleaningReportChartColors(colors);

    const fromAnalysis = !!(
        consumption && consumption.source === 'pudu_analysis'
    );

    const summary = useMemo(() => summarizeCleaningConsumption(list), [list]);
    const series = useMemo(() => {
        if (fromAnalysis) {
            return {
                water: seriesFromPuduAnalysis(
                    consumption,
                    'water',
                    localeCode,
                    periodDates
                ),
                energy: seriesFromPuduAnalysis(
                    consumption,
                    'energy',
                    localeCode,
                    periodDates
                ),
            };
        }
        return buildConsumptionChartSeries(list, periodDates, localeCode);
    }, [fromAnalysis, consumption, list, periodDates, localeCode]);

    const hourlyAxis = !!(
        (consumption && consumption.time_unit === 'hour') ||
        (periodDates &&
            periodDates.startDate &&
            periodDates.startDate === periodDates.endDate)
    );

    const waterMl = fromAnalysis
        ? Number(consumption.water_ml) || 0
        : summary.waterMl;
    const energyKwh = fromAnalysis
        ? Number(consumption.energy_kwh) || 0
        : summary.energyKwh;

    const waterFmt = formatFluidVolume(waterMl);
    const waterValue = `${waterFmt.value} ${waterFmt.unit}`;
    const energyValue = formatEnergyKwh(energyKwh);

    const waterEmpty = fromAnalysis
        ? !(Number(consumption.water_ml) > 0)
        : summary.measuredWater === 0;
    const energyEmpty = fromAnalysis
        ? !(Number(consumption.energy_kwh) > 0)
        : summary.measuredEnergy === 0;

    const warningCode = consumption && consumption.warning;
    let warningMsgId = null;
    if (warningCode === 'too_many_tasks') {
        warningMsgId =
            'page.robots.cleaning_report.utility.warning.too_many_tasks';
    } else if (
        warningCode === 'analysis_unavailable' &&
        warningCode !== 'details_syncing' &&
        warningCode !== 'details_pending'
    ) {
        warningMsgId =
            'page.robots.cleaning_report.utility.warning.analysis_unavailable';
    }

    const coverageNode = (measured, total) => {
        if (fromAnalysis || !total) return null;
        return (
            <>
                {' · '}
                <LocaleMessage
                    msg="page.robots.cleaning_report.utility.measured_coverage"
                    values={{ measured, total }}
                />
            </>
        );
    };

    return (
        <div className="row card-row">
            {warningMsgId ? (
                <div className="col-12 mb-3">
                    <Alert severity="info">
                        <LocaleMessage msg={warningMsgId} />
                    </Alert>
                </div>
            ) : null}
            <UtilityResourceCard
                titleId="page.robots.cleaning_report.utility.water.title"
                subtitleId="page.robots.cleaning_report.utility.water.subtitle"
                valueText={waterEmpty ? '—' : waterValue}
                coverage={coverageNode(
                    summary.measuredWater,
                    summary.taskCount
                )}
                series={series.water}
                color={waterColor}
                gradientId="cleaning-utility-water-fill"
                unitLabel={intl.formatMessage({
                    id: 'page.robots.cleaning_report.utility.unit_l',
                })}
                tooltipLabel={intl.formatMessage({
                    id: 'page.robots.cleaning_report.utility.legend_water',
                })}
                empty={waterEmpty}
                darkMode={darkMode}
                peak={waterEmpty ? null : seriesPeak(series.water)}
                xAxisInterval={hourlyAxis ? 2 : 'preserveStartEnd'}
            />
            <UtilityResourceCard
                titleId="page.robots.cleaning_report.utility.energy.title"
                subtitleId="page.robots.cleaning_report.utility.energy.subtitle"
                valueText={energyEmpty ? '—' : energyValue}
                coverage={coverageNode(
                    summary.measuredEnergy,
                    summary.taskCount
                )}
                series={series.energy}
                color={energyColor}
                gradientId="cleaning-utility-energy-fill"
                unitLabel={intl.formatMessage({
                    id: 'page.robots.cleaning_report.utility.unit_kwh',
                })}
                tooltipLabel={intl.formatMessage({
                    id: 'page.robots.cleaning_report.utility.legend_energy',
                })}
                empty={energyEmpty}
                darkMode={darkMode}
                peak={energyEmpty ? null : seriesPeak(series.energy)}
                xAxisInterval={hourlyAxis ? 2 : 'preserveStartEnd'}
            />
        </div>
    );
}

CleaningReportUtilityCharts.propTypes = {
    list: PropTypes.arrayOf(
        PropTypes.shape({
            report_id: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
            water_consumption_ml: PropTypes.number,
            power_consumption_wh: PropTypes.number,
            cost_water: PropTypes.number,
            cost_energy: PropTypes.number,
            start_time: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
            end_time: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            create_time: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
            created_at: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
        })
    ),
    periodDates: PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
    }),
    localeCode: PropTypes.string,
    intl: PropTypes.shape({
        formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    consumption: PropTypes.shape({
        source: PropTypes.string,
        water_ml: PropTypes.number,
        energy_kwh: PropTypes.number,
        warning: PropTypes.string,
        time_unit: PropTypes.string,
        chart: PropTypes.arrayOf(
            PropTypes.shape({
                task_time: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.number,
                ]),
                water_ml: PropTypes.number,
                energy_kwh: PropTypes.number,
            })
        ),
    }),
};

CleaningReportUtilityCharts.defaultProps = {
    list: [],
    periodDates: null,
    localeCode: 'pt_BR',
    consumption: null,
};

export default injectIntl(CleaningReportUtilityCharts);
